import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    Typography,
    CircularProgress,
    Alert,
    Box,
    Divider,
    IconButton,
    TextField,
    Grid,
    ListItemSecondaryAction,
    ListItemIcon,
} from '@mui/material';
import CommentIcon from '@mui/icons-material/Comment';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    useListChatMessages,
    useSendChatMessage,
    useUploadImage,
    useUploadChatImage,
    useDeleteChatMessage,
} from '../services/appService';
import MessageCommentsModal from './MessageCommentsModal';

// Helper to format date/time
const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    try {
        const date = new Date(dateInput);
        return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
    } catch (e) {
        return 'Invalid Date';
    }
};

function ChatMessagesModal({ open, onClose, appId, group /*, onOpenComments - Optional callback */ }) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [deletingMessageId, setDeletingMessageId] = useState(null);
    const listMessagesHook = useListChatMessages();
    const sendChatMessageHook = useSendChatMessage();
    const uploadChatImageHook = useUploadChatImage();
    const deleteChatMessageHook = useDeleteChatMessage();

    const [commentsModalOpen, setCommentsModalOpen] = useState(false);
    const [selectedMessageForComments, setSelectedMessageForComments] = useState(null);

    // State for new message form (broadcast only)
    const [newMessageText, setNewMessageText] = useState('');
    const [newMessageImageFile, setNewMessageImageFile] = useState(null);
    const [newMessageImagePreview, setNewMessageImagePreview] = useState(null);
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState('');
    const imageInputRef = useRef(null);

    // Effect to revoke object URLs
    useEffect(() => {
        const previewUrl = newMessageImagePreview;
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [newMessageImagePreview]);

    const fetchMessages = useCallback(async () => {
        if (!appId || !group?.id) {
            setMessages([]); // Clear messages if no group is selected
            return;
        }
        setLoading(true);
        setError('');
        try {
            const data = await listMessagesHook(appId, group.id);
            setMessages(data || []);
        } catch (err) {
            console.error("Error fetching messages:", err);
            setError(err.message || 'Failed to load messages.');
        } finally {
            setLoading(false);
        }
    }, [appId, group?.id, listMessagesHook]);

    useEffect(() => {
        if (open) {
            fetchMessages();
            // Reset send form state on open
            setNewMessageText('');
            setNewMessageImageFile(null);
            if (newMessageImagePreview) URL.revokeObjectURL(newMessageImagePreview);
            setNewMessageImagePreview(null);
            setSending(false);
            setSendError('');
            setDeletingMessageId(null);
        } else {
            // Reset list state when modal closes
            setMessages([]);
            setError('');
        }
    }, [open, fetchMessages]);

    const handleOpenComments = (message) => {
        setSelectedMessageForComments(message);
        setCommentsModalOpen(true);
        // Optionally call parent handler if needed: onOpenComments(message);
    };

    const handleCloseCommentsModal = () => {
        setSelectedMessageForComments(null);
        setCommentsModalOpen(false);
    };

    // --- Delete Message Handler ---
    const handleDeleteMessage = async (messageId) => {
        if (!appId || !group?.id || !messageId) return;

        // Simple confirmation
        if (!window.confirm(`Are you sure you want to delete this message (ID: ${messageId})? This cannot be undone.`)) {
            return;
        }

        setDeletingMessageId(messageId); // Set loading state for this specific message
        setError(''); // Clear previous errors
        try {
            await deleteChatMessageHook(appId, group.id, messageId);
            // Refresh messages after successful deletion
            fetchMessages();
        } catch (err) {
            console.error("Error deleting message:", err);
            // Show error specific to deletion
            setError(`Failed to delete message ${messageId}: ${err.message || 'Unknown error'}`);
        } finally {
            setDeletingMessageId(null); // Clear loading state regardless of success/failure
        }
    };

    // --- New Message Handlers (Broadcast Only) ---

    const handleFileSelect = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (newMessageImagePreview) {
            URL.revokeObjectURL(newMessageImagePreview); // Revoke previous local preview
        }
        setNewMessageImageFile(file);
        setNewMessageImagePreview(URL.createObjectURL(file));
        setSendError(''); // Clear error on new file select
    };

    const handleRemoveImage = () => {
        if (newMessageImagePreview) {
            URL.revokeObjectURL(newMessageImagePreview);
        }
        setNewMessageImageFile(null);
        setNewMessageImagePreview(null);
        if (imageInputRef.current) {
            imageInputRef.current.value = null; // Reset file input
        }
    };

    const handleSendMessage = async () => {
        if (!appId || !group?.id || group.type !== 'broadcast') return;
        if (!newMessageText && !newMessageImageFile) {
            setSendError("Message must include text or an image.");
            return;
        }

        setSending(true);
        setSendError('');
        let uploadedImageUrl = null;

        try {
            // 1. Upload image if selected
            if (newMessageImageFile) {
                const uploadResult = await uploadChatImageHook(appId, newMessageImageFile);
                uploadedImageUrl = uploadResult.url;
            }

            // 2. Send message via API
            const messageData = {
                text: newMessageText || null,
                image_url: uploadedImageUrl,
            };
            await sendChatMessageHook(appId, group.id, messageData);

            // 3. Success: Clear form and refresh messages
            setNewMessageText('');
            handleRemoveImage(); // Clears file, preview, and input ref
            fetchMessages(); // Refresh the list

        } catch (err) {
            console.error("Error sending message:", err);
            setSendError(err.message || "Failed to send message.");
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
                <DialogTitle>Messages in "{group?.name || 'Chat'}"</DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column' }}>
                    {/* Message List Area */}
                    <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: group?.type === 'broadcast' ? 2 : 0 }}>
                        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                        {loading && !deletingMessageId ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                                <CircularProgress />
                            </Box>
                        ) : messages.length === 0 && !loading ? (
                            <Typography sx={{ textAlign: 'center', mt: 2 }}>No messages found in this group.</Typography>
                        ) : (
                            <List disablePadding>
                                {messages.map((msg, index) => (
                                    <React.Fragment key={msg.id}>
                                        <ListItem alignItems="flex-start" sx={{ pr: (group?.type === 'broadcast' || group?.type === 'open') ? theme => theme.spacing(10) : 0 }}>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                                            {msg.text}
                                                            {msg.image_url && (
                                                                <Box sx={{ mt: msg.text ? 1 : 0 }}>
                                                                    <img
                                                                        src={msg.image_url}
                                                                        alt="Chat image"
                                                                        style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '4px' }}
                                                                        loading="lazy"
                                                                    />
                                                                </Box>
                                                            )}
                                                            {!msg.text && !msg.image_url && <i>No Content</i>}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <>
                                                            <Typography
                                                                sx={{ display: 'inline' }}
                                                                component="span"
                                                                variant="body2"
                                                                color="text.primary"
                                                            >
                                                                {msg.sender || 'Unknown Sender'}
                                                            </Typography>
                                                            {` — ${formatDate(msg.created_at)}`}
                                                        </>
                                                    }
                                                />
                                            </Box>
                                            {/* Show actions only for broadcast or open groups */}
                                            {(group?.type === 'broadcast' || group?.type === 'open') && (
                                                <ListItemSecondaryAction sx={{ top: '50%', right: theme => theme.spacing(2), transform: 'translateY(-50%)' }}>
                                                    {deletingMessageId === msg.id ? (
                                                        <CircularProgress size={20} sx={{ display: 'flex', alignItems: 'center' }} />
                                                    ) : (
                                                        <>
                                                            <IconButton
                                                                edge="end"
                                                                aria-label="comments"
                                                                onClick={() => handleOpenComments(msg)}
                                                                size="small"
                                                                disabled={!!deletingMessageId}
                                                                sx={{ mr: 0.5, display: group?.type === 'broadcast' ? 'inline-flex' : 'none' }}
                                                            >
                                                                <CommentIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                edge="end"
                                                                aria-label="delete"
                                                                onClick={() => handleDeleteMessage(msg.id)}
                                                                size="small"
                                                                color="error"
                                                                disabled={!!deletingMessageId}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </>
                                                    )}
                                                </ListItemSecondaryAction>
                                            )}
                                        </ListItem>
                                        {index < messages.length - 1 && <Divider variant="inset" component="li" />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </Box>

                    {/* New Message Form (Broadcast Only) */}
                    {group?.type === 'broadcast' && (
                        <Box sx={{ mt: 2, borderTop: '1px solid', borderColor: 'divider', pt: 2, flexShrink: 0 }}>
                            <Typography variant="h6" gutterBottom>Send New Message</Typography>
                             {sendError && <Alert severity="error" sx={{ mb: 2 }}>{sendError}</Alert>}
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Message Text (Optional)"
                                        variant="outlined"
                                        multiline
                                        rows={3}
                                        value={newMessageText}
                                        onChange={(e) => setNewMessageText(e.target.value)}
                                        disabled={sending}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                     <Box sx={{ display: 'flex', alignItems: 'center', border: '1px dashed grey', padding: 1, borderRadius: 1, height: '74px' }}>
                                         {sending && newMessageImageFile ? (
                                             <CircularProgress size={50} sx={{ mr: 1 }} />
                                         ) : newMessageImagePreview ? (
                                             <img src={newMessageImagePreview} alt="Preview" style={{ width: '50px', height: '50px', objectFit: 'cover', marginRight: '8px', borderRadius: '4px' }} />
                                         ) : (
                                             <Box sx={{ width: 50, height: 50, mr: 1, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>
                                                 <PhotoCamera color="disabled" />
                                             </Box>
                                         )}
                                         <Button
                                            variant="outlined"
                                            component="label"
                                            disabled={sending}
                                            startIcon={<PhotoCamera />}
                                            size="small"
                                            sx={{ mr: 1, flexShrink: 0 }}
                                        >
                                            {newMessageImagePreview ? 'Change Image' : 'Add Image'}
                                            <input
                                                ref={imageInputRef}
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                onChange={handleFileSelect}
                                            />
                                        </Button>
                                         {newMessageImagePreview && (
                                             <IconButton
                                                size="small"
                                                onClick={handleRemoveImage}
                                                disabled={sending}
                                                sx={{ flexShrink: 0 }}
                                            >
                                                <ClearIcon />
                                            </IconButton>
                                         )}
                                     </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSendMessage}
                                        disabled={sending || (!newMessageText && !newMessageImageFile)}
                                        sx={{ minWidth: 120 }}
                                    >
                                        {sending ? <CircularProgress size={24} /> : 'Send Message'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={sending || !!deletingMessageId}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Render the Comments Modal */}
            <MessageCommentsModal
                open={commentsModalOpen}
                onClose={handleCloseCommentsModal}
                appId={appId}
                groupId={group?.id}
                message={selectedMessageForComments}
            />
        </>
    );
}

export default ChatMessagesModal; 